import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, FormikProvider, useFormik } from 'formik';

import { Icon, Button, FormikDate } from '@febos-io/design';

import useTranslation from '../../hooks/useTranslation';
import { useConfigurationDateFilter } from './hooks/useConfigurationDateFilter';

export function DateFilter({
  onFilter = () => null,
  title,
  titleCustomRange
}) {
  const { t } = useTranslation();
  const { configDateRangeFilter } = useConfigurationDateFilter();
  const [filter, setFilter] = useState(configDateRangeFilter.dateRangeMonth);

  const selectFilterDate = useCallback((filterDate) => {
    const today = new Date();
    const rangeFrom = today.toISOString().split('T')[0];
    const firstDayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    let rangeUntil;

    switch (filterDate.value) {
      case 'thisMonth':
        rangeUntil = firstDayMonth.toISOString().split('T')[0];
        break;
      case 'last4Week':
        rangeUntil = new Date(today.setDate(today.getDate() - 28)).toISOString().split('T')[0];
        break;
      case 'thisMonthAndPrevious':
        rangeUntil = new Date(firstDayMonth.setMonth(firstDayMonth.getMonth() - 1)).toISOString().split('T')[0];
        break;
      case 'last3Months':
        rangeUntil = new Date(today.setMonth(today.getMonth() - 3)).toISOString().split('T')[0];
        break;
      case 'last6Months':
        rangeUntil = new Date(today.setMonth(today.getMonth() - 6)).toISOString().split('T')[0];
        break;
      default:
        break;
    }
    onFilter({
      rangeFrom,
      rangeUntil
    });
  }, [onFilter]);

  const onSubmit = (values) => {
    onFilter({
      rangeFrom: new Date(values.rangeFrom).toISOString().split('T')[0],
      rangeUntil: new Date(values.rangeUntil).toISOString().split('T')[0]
    });
  };

  const formik = useFormik({
    initialValues: {
      rangeFrom: '',
      rangeUntil: ''
    },
    onSubmit: onSubmit
  });

  useEffect(() => {
    if (filter.value !== 'customRange') {
      selectFilterDate(filter);
    }
  }, [filter, selectFilterDate]);

  return (
    <div>
      <div className="d-inline-block">
        {filter.value !== 'customRange' && (
          `${title} ${filter.label}`
        )}
        {filter.value === 'customRange' && (
          <FormikProvider value={formik}>
            <Form className="row align-items-center justify-content-start">
              <div className="col-auto">
                {titleCustomRange}
              </div>
              <div className="col-auto">
                <FormikDate
                  name="rangeFrom"
                  type="date"
                />
              </div>
              {t('and')}
              <div className="col-auto">
                <FormikDate
                  name="rangeUntil"
                  type="date"
                />
              </div>
              <div className="col-auto">
                <Button
                  type="submit"
                  icon="search"
                  outline
                  size="sm"
                />
              </div>
            </Form>
          </FormikProvider>
        )}
      </div>
      <div className="d-inline-block">
        <div className="dropdown">
          <button
            className="btn btn-group text-primary btn-sm rounded-pill"
            type="button"
            id="dropdownDateFilter"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            aria-haspopup="true"
          >
            {t('Change')}
            <Icon
              className="ms-1"
              icon="chevron-down"
            />
          </button>
          <ul
            className="dropdown-menu dropdown-menu-end"
            aria-labelledby="dropdownDateFilter"
          >
            {Object.entries(configDateRangeFilter)
              .map(([dateFilter, config]) => (
                <li key={dateFilter}>
                  <Button
                    outline
                    theme="white"
                    className="dropdown-item d-block w-100"
                    onClick={() => setFilter(config)}
                  >
                    {config.label}
                  </Button>
                </li>
              ))
            }
          </ul>
        </div>
      </div>
    </div>
  );
}

DateFilter.propTypes = {
  onFilter: PropTypes.func,
  title: PropTypes.string.isRequired,
  titleCustomRange: PropTypes.string.isRequired
};

export default DateFilter;
